import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, BookOpen, X, Link as LinkIcon, Upload, FileText, ChevronDown, ChevronUp, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  createLesson,
  getLessons,
  updateLesson,
  deleteLesson,
  getUnits
} from '../firebase/lessonPlannerService';
import { getAssignments } from '../firebase/assignmentService';
import { uploadLessonResource } from '../firebase/storageService';
import { parseDateLocal, formatDateLocal } from '../utils/dateUtils';
import DashboardLayout from './DashboardLayout';
import './Lessons.css';

function Lessons() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [lessons, setLessons] = useState([]);
  const [units, setUnits] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [viewMode, setViewMode] = useState('all');
  const [selectedUnit, setSelectedUnit] = useState('all');
  const [expandedLessons, setExpandedLessons] = useState({});
  const [formData, setFormData] = useState({
    lessonNumber: '',
    title: '',
    learningObjective: '',
    lessonSummary: '',
    description: '',
    unitIds: [],
    assignmentIds: [],
    duration: 45,
    startDate: '',
    endDate: '',
    objectives: [''],
    materials: [''],
    procedures: '',
    assessment: '',
    standards: [''],
    resources: []
  });
  const [uploadingFile, setUploadingFile] = useState(null);
  const [draggedLesson, setDraggedLesson] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1); // Adjust to Monday
    return new Date(today.setDate(diff));
  });

  useEffect(() => {
    if (!currentUser) {
      navigate('/');
      return;
    }
    loadData();
  }, [currentUser, navigate]);

  const loadData = async () => {
    setLoading(true);
    const [lessonsResult, unitsResult, assignmentsResult] = await Promise.all([
      getLessons(currentUser.id),
      getUnits(currentUser.id),
      getAssignments(currentUser.uid)
    ]);

    if (lessonsResult.success) {
      setLessons(lessonsResult.lessons);
    }
    if (unitsResult.success) {
      setUnits(unitsResult.units);
    }
    if (assignmentsResult.success) {
      setAssignments(assignmentsResult.assignments);
    }
    setLoading(false);
  };

  // Bump lesson numbers starting from a specific number within the same unit
  const bumpLessonNumbers = async (startNumber, unitIds, excludeLessonId = null) => {
    const lessonNum = parseInt(startNumber);
    if (isNaN(lessonNum) || !unitIds || unitIds.length === 0) return;

    // Get all lessons in the same unit(s) with numbers >= startNumber
    const lessonsToUpdate = lessons.filter(lesson => {
      if (excludeLessonId && lesson.id === excludeLessonId) return false;

      // Check if lesson is in one of the same units
      const lessonUnitIds = lesson.unitIds || (lesson.unitId ? [lesson.unitId] : []);
      const isSameUnit = lessonUnitIds.some(unitId => unitIds.includes(unitId));

      if (!isSameUnit) return false;

      const num = parseInt(lesson.lessonNumber);
      return !isNaN(num) && num >= lessonNum;
    });

    // Update each lesson by incrementing its number
    for (const lesson of lessonsToUpdate) {
      const currentNum = parseInt(lesson.lessonNumber);
      await updateLesson(lesson.id, {
        lessonNumber: (currentNum + 1).toString()
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.unitIds.length === 0) {
      alert('Please select at least one unit for this lesson.');
      return;
    }

    // Filter out empty array items
    const objectives = formData.objectives.filter(o => o.trim() !== '');
    const materials = formData.materials.filter(m => m.trim() !== '');
    const standards = formData.standards.filter(s => s.trim() !== '');

    const lessonData = {
      ...formData,
      objectives,
      materials,
      standards,
      duration: parseInt(formData.duration)
    };

    console.log('Saving lesson with resources:', lessonData.resources);

    // Check if we need to bump lesson numbers within the same unit
    if (formData.lessonNumber && formData.lessonNumber.trim() !== '' && formData.unitIds.length > 0) {
      const newLessonNum = parseInt(formData.lessonNumber);
      if (!isNaN(newLessonNum)) {
        // If editing, check if the number changed
        if (editingLesson) {
          const oldNum = parseInt(editingLesson.lessonNumber);
          if (oldNum !== newLessonNum) {
            // Bump lessons at the new number position within the same unit(s)
            await bumpLessonNumbers(newLessonNum, formData.unitIds, editingLesson.id);
          }
        } else {
          // For new lessons, bump if number exists within the same unit(s)
          await bumpLessonNumbers(newLessonNum, formData.unitIds);
        }
      }
    }

    const userId = currentUser.uid || currentUser.id;

    if (editingLesson) {
      const result = await updateLesson(editingLesson.id, lessonData);
      console.log('Update result:', result);
      if (result.success) {
        loadData();
        closeModal();
      }
    } else {
      const result = await createLesson(userId, lessonData);
      console.log('Create result:', result);
      if (result.success) {
        loadData();
        closeModal();
      }
    }
  };

  const handleEdit = (lesson) => {
    setEditingLesson(lesson);
    setFormData({
      lessonNumber: lesson.lessonNumber || '',
      title: lesson.title,
      learningObjective: lesson.learningObjective || '',
      lessonSummary: lesson.lessonSummary || '',
      description: lesson.description || '',
      unitIds: lesson.unitIds || (lesson.unitId ? [lesson.unitId] : []),
      assignmentIds: lesson.assignmentIds || [],
      duration: lesson.duration || 45,
      startDate: lesson.startDate || lesson.scheduledDate || '',
      endDate: lesson.endDate || '',
      objectives: lesson.objectives && lesson.objectives.length > 0 ? lesson.objectives : [''],
      materials: lesson.materials && lesson.materials.length > 0 ? lesson.materials : [''],
      procedures: lesson.procedures || '',
      assessment: lesson.assessment || '',
      standards: lesson.standards && lesson.standards.length > 0 ? lesson.standards : [''],
      resources: lesson.resources || []
    });
    setShowModal(true);
  };

  const handleDelete = async (lessonId) => {
    if (window.confirm('Are you sure you want to delete this lesson?')) {
      const result = await deleteLesson(lessonId);
      if (result.success) {
        loadData();
      }
    }
  };

  const handleUnitToggle = (unitId) => {
    setFormData(prev => ({
      ...prev,
      unitIds: prev.unitIds.includes(unitId)
        ? prev.unitIds.filter(id => id !== unitId)
        : [...prev.unitIds, unitId]
    }));
  };

  const handleAssignmentToggle = (assignmentId) => {
    setFormData(prev => ({
      ...prev,
      assignmentIds: prev.assignmentIds.includes(assignmentId)
        ? prev.assignmentIds.filter(id => id !== assignmentId)
        : [...prev.assignmentIds, assignmentId]
    }));
  };

  const getUnitNames = (unitIds) => {
    if (!unitIds || unitIds.length === 0) return 'No units assigned';
    return unitIds
      .map(id => units.find(u => u.id === id)?.title)
      .filter(Boolean)
      .join(', ');
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingLesson(null);
    setFormData({
      lessonNumber: '',
      title: '',
      learningObjective: '',
      lessonSummary: '',
      description: '',
      unitIds: [],
      assignmentIds: [],
      duration: 45,
      startDate: '',
      endDate: '',
      objectives: [''],
      materials: [''],
      procedures: '',
      assessment: '',
      standards: [''],
      resources: []
    });
  };

  const handleArrayFieldChange = (field, index, value) => {
    const newArray = [...formData[field]];
    newArray[index] = value;
    setFormData({ ...formData, [field]: newArray });
  };

  const addArrayField = (field) => {
    setFormData({ ...formData, [field]: [...formData[field], ''] });
  };

  const removeArrayField = (field, index) => {
    const newArray = formData[field].filter((_, i) => i !== index);
    setFormData({ ...formData, [field]: newArray });
  };

  const addResource = () => {
    setFormData({
      ...formData,
      resources: [
        ...formData.resources,
        { description: '', type: 'link', url: '', fileName: '', fileUrl: '' }
      ]
    });
  };

  const removeResource = (index) => {
    const newResources = formData.resources.filter((_, i) => i !== index);
    setFormData({ ...formData, resources: newResources });
  };

  const updateResource = (index, field, value) => {
    const newResources = [...formData.resources];
    newResources[index] = { ...newResources[index], [field]: value };
    setFormData({ ...formData, resources: newResources });
  };

  const handleFileUpload = async (index, file) => {
    if (!file) return;

    setUploadingFile(index);

    // For new lessons, we'll use a temporary ID
    const lessonId = editingLesson?.id || 'temp_' + Date.now();
    const userId = currentUser.uid || currentUser.id;

    console.log('Uploading file:', { userId, lessonId, fileName: file.name });

    const result = await uploadLessonResource(userId, lessonId, file);

    console.log('Upload result:', result);

    if (result.success) {
      // Update all fields at once to avoid state race condition
      const newResources = [...formData.resources];
      newResources[index] = {
        ...newResources[index],
        fileUrl: result.url,
        fileName: result.fileName,
        filePath: result.filePath
      };
      setFormData({ ...formData, resources: newResources });
      console.log('Resource updated with URL:', result.url);
    } else {
      console.error('Upload failed:', result.error);
      alert('Failed to upload file. Please try again.');
    }

    setUploadingFile(null);
  };

  const toggleExpanded = (lessonId) => {
    setExpandedLessons(prev => ({
      ...prev,
      [lessonId]: !prev[lessonId]
    }));
  };

  const openResourcesMenu = (lesson, e) => {
    e.stopPropagation();
    if (!lesson.resources || lesson.resources.length === 0) return;

    // If only one resource, open it directly
    if (lesson.resources.length === 1) {
      const resource = lesson.resources[0];
      window.open(resource.fileUrl || resource.url, '_blank');
    } else {
      // If multiple resources, show them in a simple way by expanding the lesson
      setExpandedLessons(prev => ({
        ...prev,
        [lesson.id]: true
      }));
    }
  };

  const getUnitTitle = (unitId) => {
    const unit = units.find(u => u.id === unitId);
    return unit?.title || 'No Unit';
  };

  // Week navigation functions
  const previousWeek = () => {
    const newWeekStart = new Date(currentWeekStart);
    newWeekStart.setDate(newWeekStart.getDate() - 7);
    setCurrentWeekStart(newWeekStart);
  };

  const nextWeek = () => {
    const newWeekStart = new Date(currentWeekStart);
    newWeekStart.setDate(newWeekStart.getDate() + 7);
    setCurrentWeekStart(newWeekStart);
  };

  const goToCurrentWeek = () => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1); // Adjust to Monday
    setCurrentWeekStart(new Date(today.setDate(diff)));
  };

  // Drag and drop handlers for schedule list
  const handleScheduleDragStart = (e, lesson) => {
    setDraggedLesson(lesson);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('lessonId', lesson.id);
  };

  // Handler for dropping on a specific lesson (for reordering within a date)
  const handleLessonDrop = async (e, targetLesson) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverIndex(null);

    if (!draggedLesson || draggedLesson.id === targetLesson.id) {
      setDraggedLesson(null);
      return;
    }

    const targetDate = targetLesson.startDate || targetLesson.scheduledDate;
    const draggedDate = draggedLesson.startDate || draggedLesson.scheduledDate;
    const dateField = draggedLesson.startDate ? 'startDate' : 'scheduledDate';

    // Get all lessons for the target date
    const dateLessons = lessons.filter(lesson => {
      const lessonDate = lesson.startDate || lesson.scheduledDate;
      return lessonDate === targetDate || lessonDate?.startsWith(targetDate);
    }).sort((a, b) => {
      const numA = parseInt(a.lessonNumber) || 999999;
      const numB = parseInt(b.lessonNumber) || 999999;
      return numA - numB;
    });

    // If dragging from a different date, update the date first
    if (draggedDate !== targetDate) {
      await updateLesson(draggedLesson.id, {
        [dateField]: targetDate
      });
      await loadData();
    }

    // Find positions
    const targetIndex = dateLessons.findIndex(l => l.id === targetLesson.id);
    let draggedIndex = dateLessons.findIndex(l => l.id === draggedLesson.id);

    // If we just moved from another date, the lesson won't be in dateLessons yet
    // In that case, we'll insert it at the target position
    if (draggedIndex === -1) {
      // Reload to get updated data
      await loadData();
      const updatedDateLessons = lessons.filter(lesson => {
        const lessonDate = lesson.startDate || lesson.scheduledDate;
        return lessonDate === targetDate || lessonDate?.startsWith(targetDate);
      }).sort((a, b) => {
        const numA = parseInt(a.lessonNumber) || 999999;
        const numB = parseInt(b.lessonNumber) || 999999;
        return numA - numB;
      });

      // Remove dragged lesson and insert at target position
      const filtered = updatedDateLessons.filter(l => l.id !== draggedLesson.id);
      const newTargetIndex = filtered.findIndex(l => l.id === targetLesson.id);
      filtered.splice(newTargetIndex, 0, draggedLesson);

      // Renumber
      for (let i = 0; i < filtered.length; i++) {
        await updateLesson(filtered[i].id, { lessonNumber: (i + 1).toString() });
      }
    } else {
      // Reorder within same date
      const reordered = [...dateLessons];
      const [removed] = reordered.splice(draggedIndex, 1);
      reordered.splice(targetIndex, 0, removed);

      // Renumber
      for (let i = 0; i < reordered.length; i++) {
        await updateLesson(reordered[i].id, { lessonNumber: (i + 1).toString() });
      }
    }

    setDraggedLesson(null);
    await loadData();

    // Also update the old date if different
    if (draggedDate !== targetDate) {
      await updateLessonNumbersForDate(draggedDate);
    }
  };

  const handleLessonDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDateSlotDragOver = (e, dateString) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIndex !== dateString) {
      setDragOverIndex(dateString);
    }
  };

  const handleDateSlotDragLeave = (e, dateString) => {
    e.preventDefault();
    // Only clear if we're leaving to something outside the date slot
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
      setDragOverIndex(null);
    }
  };

  const handleDateSlotDrop = async (e, targetDate) => {
    e.preventDefault();
    setDragOverIndex(null);

    if (!draggedLesson) {
      return;
    }

    const dateField = draggedLesson.startDate ? 'startDate' : 'scheduledDate';
    const oldDate = draggedLesson.startDate || draggedLesson.scheduledDate;

    // Update the lesson with the new date
    await updateLesson(draggedLesson.id, {
      [dateField]: targetDate
    });

    setDraggedLesson(null);

    // Reload data and update lesson numbers for both old and new dates
    await loadData();
    await updateLessonNumbersForDate(oldDate);
    await updateLessonNumbersForDate(targetDate);
  };

  // Update lesson numbers for a specific date
  const updateLessonNumbersForDate = async (dateString) => {
    if (!dateString) return;

    // Get all lessons for this date
    const dateLessons = lessons.filter(lesson => {
      const lessonDate = lesson.startDate || lesson.scheduledDate;
      return lessonDate === dateString || lessonDate?.startsWith(dateString);
    }).sort((a, b) => {
      // Sort by existing lesson number to maintain order
      const numA = parseInt(a.lessonNumber) || 999999;
      const numB = parseInt(b.lessonNumber) || 999999;
      return numA - numB;
    });

    // Renumber sequentially
    for (let i = 0; i < dateLessons.length; i++) {
      const lesson = dateLessons[i];
      const newNumber = (i + 1).toString();
      if (lesson.lessonNumber !== newNumber) {
        await updateLesson(lesson.id, {
          lessonNumber: newNumber
        });
      }
    }
  };

  // Unit view drag handlers
  const handleUnitViewDragStart = (e, lesson, index) => {
    setDraggedLesson({ lesson, index });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleUnitViewDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleUnitViewDragLeave = (e) => {
    e.preventDefault();
    setDragOverIndex(null);
  };

  const handleUnitViewDrop = async (e, dropIndex) => {
    e.preventDefault();
    e.stopPropagation();

    if (!draggedLesson || draggedLesson.index === dropIndex) {
      setDraggedLesson(null);
      setDragOverIndex(null);
      return;
    }

    // Get the sorted filtered lessons
    const sortedLessons = filteredLessons.sort((a, b) => {
      // First sort by unit number
      const unitNumA = getUnitOrderNumber(a.unitIds || (a.unitId ? [a.unitId] : []));
      const unitNumB = getUnitOrderNumber(b.unitIds || (b.unitId ? [b.unitId] : []));

      if (unitNumA !== unitNumB) {
        return unitNumA - unitNumB;
      }

      // Then sort by lesson number within the same unit
      const numA = parseInt(a.lessonNumber) || 999999;
      const numB = parseInt(b.lessonNumber) || 999999;
      return numA - numB;
    });

    const draggedLessonData = sortedLessons[draggedLesson.index];
    const targetLessonData = sortedLessons[dropIndex];

    // Check if they're in the same unit
    const draggedUnitIds = draggedLessonData.unitIds || (draggedLessonData.unitId ? [draggedLessonData.unitId] : []);
    const targetUnitIds = targetLessonData.unitIds || (targetLessonData.unitId ? [targetLessonData.unitId] : []);

    // Only allow reordering within the same unit
    const isSameUnit = draggedUnitIds.some(unitId => targetUnitIds.includes(unitId));

    if (!isSameUnit) {
      setDraggedLesson(null);
      setDragOverIndex(null);
      return;
    }

    // Get all lessons in the same unit
    const unitLessons = sortedLessons.filter(lesson => {
      const lessonUnitIds = lesson.unitIds || (lesson.unitId ? [lesson.unitId] : []);
      return lessonUnitIds.some(unitId => draggedUnitIds.includes(unitId));
    });

    // Find indices within the unit
    const dragIndexInUnit = unitLessons.findIndex(l => l.id === draggedLessonData.id);
    const dropIndexInUnit = unitLessons.findIndex(l => l.id === targetLessonData.id);

    // Create new order by moving the dragged lesson to the drop position within the unit
    const reorderedUnitLessons = [...unitLessons];
    const [movedLesson] = reorderedUnitLessons.splice(dragIndexInUnit, 1);
    reorderedUnitLessons.splice(dropIndexInUnit, 0, movedLesson);

    // Update lesson numbers for all lessons in this unit
    const updatePromises = reorderedUnitLessons.map((lesson, index) => {
      const newLessonNumber = (index + 1).toString();
      if (lesson.lessonNumber !== newLessonNumber) {
        return updateLesson(lesson.id, { ...lesson, lessonNumber: newLessonNumber });
      }
      return Promise.resolve();
    });

    await Promise.all(updatePromises);

    setDraggedLesson(null);
    setDragOverIndex(null);
    await loadData();
  };

  // Update lesson numbers based on chronological order (global)
  const updateAllLessonNumbers = async () => {
    const scheduledLessons = getScheduledLessons();

    for (let i = 0; i < scheduledLessons.length; i++) {
      const lesson = scheduledLessons[i];
      if (lesson.lessonNumber !== (i + 1).toString()) {
        await updateLesson(lesson.id, {
          lessonNumber: (i + 1).toString()
        });
      }
    }
  };


  // Get lessons that have dates and are on weekdays (Mon-Fri)
  const getScheduledLessons = () => {
    return lessons
      .filter(lesson => {
        const dateField = lesson.startDate || lesson.scheduledDate;
        if (!dateField) return false;

        const lessonDate = parseDateLocal(dateField);
        const dayOfWeek = lessonDate.getDay();
        // Only include Mon-Fri (1-5)
        return dayOfWeek >= 1 && dayOfWeek <= 5;
      })
      .sort((a, b) => {
        const dateA = parseDateLocal(a.startDate || a.scheduledDate);
        const dateB = parseDateLocal(b.startDate || b.scheduledDate);
        return dateA - dateB;
      });
  };

  // Generate weekday date slots for the schedule view (Mon-Fri of current week)
  const getWeekdayDateSlots = () => {
    const dateSlots = [];
    // Generate Monday through Friday of the current week
    for (let i = 0; i < 5; i++) {
      const date = new Date(currentWeekStart);
      date.setDate(currentWeekStart.getDate() + i);
      dateSlots.push(date);
    }
    return dateSlots;
  };

  // Group lessons by date
  const getLessonsByDate = (dateString) => {
    return lessons.filter(lesson => {
      const dateField = lesson.startDate || lesson.scheduledDate;
      if (!dateField) return false;

      return dateField === dateString;
    });
  };

  // Get assignments by date
  const getAssignmentsByDate = (dateString) => {
    return assignments.filter(assignment => {
      if (!assignment.assignmentDate) return false;

      const assignDateStr = assignment.assignmentDate.toDate
        ? assignment.assignmentDate.toDate().toISOString().split('T')[0]
        : assignment.assignmentDate;

      return assignDateStr === dateString;
    });
  };

  const filteredLessons = viewMode === 'unit' && selectedUnit !== 'all'
    ? lessons.filter(l => {
        // Support both old unitId and new unitIds
        if (l.unitIds && Array.isArray(l.unitIds)) {
          return l.unitIds.includes(selectedUnit);
        }
        return l.unitId === selectedUnit;
      })
    : lessons;

  // Helper function to get unit order number
  const getUnitOrderNumber = (unitIds) => {
    if (!unitIds || unitIds.length === 0) return 999999;
    const firstUnitId = unitIds[0];
    const unit = units.find(u => u.id === firstUnitId);
    return unit?.unitNumber || 999999;
  };

  // Helper function to get color for a unit
  const getUnitColor = (unitIds) => {
    if (!unitIds || unitIds.length === 0) return 'default';
    const unitNumber = getUnitOrderNumber(unitIds);
    const colors = [
      'unit-color-1',  // Purple/Blue
      'unit-color-2',  // Green
      'unit-color-3',  // Orange
      'unit-color-4',  // Pink
      'unit-color-5',  // Teal
      'unit-color-6',  // Red
      'unit-color-7',  // Indigo
      'unit-color-8',  // Yellow
    ];
    return colors[(unitNumber - 1) % colors.length] || 'default';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading lessons...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="lessons-container">
        <div className="page-header">
          <div>
            <h1>Lessons</h1>
            <p className="subtitle">Manage your lesson plans</p>
          </div>
          <button onClick={() => setShowModal(true)} className="btn-primary">
            <Plus size={20} />
            New Lesson
          </button>
        </div>

        {/* View Mode Toggle */}
        <div className="view-controls">
          <div className="view-toggle">
            <button
              onClick={() => setViewMode('all')}
              className={`toggle-btn ${viewMode === 'all' ? 'active' : ''}`}
            >
              All Lessons
            </button>
            <button
              onClick={() => setViewMode('unit')}
              className={`toggle-btn ${viewMode === 'unit' ? 'active' : ''}`}
            >
              By Unit
            </button>
            <button
              onClick={() => setViewMode('schedule')}
              className={`toggle-btn ${viewMode === 'schedule' ? 'active' : ''}`}
            >
              Schedule List
            </button>
          </div>

          {viewMode === 'unit' && units.length > 0 && (
            <select
              value={selectedUnit}
              onChange={(e) => setSelectedUnit(e.target.value)}
              className="unit-filter"
            >
              <option value="all">All Units</option>
              {units.map(unit => (
                <option key={unit.id} value={unit.id}>{unit.title}</option>
              ))}
            </select>
          )}
        </div>

        {viewMode === 'schedule' ? (
          // Schedule List View
          (() => {
            const dateSlots = getWeekdayDateSlots();
            const weekStart = currentWeekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            const weekEnd = new Date(currentWeekStart);
            weekEnd.setDate(weekEnd.getDate() + 4);
            const weekEndStr = weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

            return (
              <div className="schedule-list">
                <div className="schedule-list-header">
                  <div className="week-controls">
                    <button onClick={previousWeek} className="week-nav-btn">
                      <ChevronLeft size={18} />
                    </button>
                    <button onClick={goToCurrentWeek} className="today-btn">
                      Current Week
                    </button>
                    <button onClick={nextWeek} className="week-nav-btn">
                      <ChevronRight size={18} />
                    </button>
                  </div>
                  <h3 className="week-range">{weekStart} - {weekEndStr}</h3>
                  <p className="schedule-hint">Drag lessons to move them to different dates.</p>
                </div>
                {dateSlots.map((date) => {
                  const dateString = formatDateLocal(date);
                  const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
                  const formattedDate = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                  const dateLessons = getLessonsByDate(dateString);
                  const dateAssignments = getAssignmentsByDate(dateString);
                  const isDragOver = dragOverIndex === dateString;
                  const isToday = date.toDateString() === new Date().toDateString();

                  return (
                    <div
                      key={dateString}
                      className={`schedule-date-slot ${isDragOver ? 'drag-over' : ''} ${isToday ? 'today' : ''}`}
                      onDragOver={(e) => handleDateSlotDragOver(e, dateString)}
                      onDragLeave={(e) => handleDateSlotDragLeave(e, dateString)}
                      onDrop={(e) => handleDateSlotDrop(e, dateString)}
                    >
                      <div className="schedule-date-info">
                        <div className="schedule-day">{dayName}</div>
                        <div className="schedule-date">{formattedDate}</div>
                      </div>
                      <div className="schedule-lessons-container">
                        {dateLessons.length > 0 && (
                          dateLessons.map((lesson) => {
                            const unitColor = getUnitColor(lesson.unitIds || (lesson.unitId ? [lesson.unitId] : []));
                            return (
                            <div
                              key={lesson.id}
                              className={`schedule-lesson-item ${unitColor}`}
                              draggable={true}
                              onDragStart={(e) => handleScheduleDragStart(e, lesson)}
                              onDragOver={handleLessonDragOver}
                              onDrop={(e) => handleLessonDrop(e, lesson)}
                            >
                              <div className="schedule-drag-handle">
                                <div className="drag-dots"></div>
                              </div>
                              <div className="schedule-lesson-info">
                                <div className="schedule-lesson-title">
                                  📚 {lesson.lessonNumber && <span className="lesson-number">Lesson {lesson.lessonNumber}: </span>}
                                  {lesson.title}
                                </div>
                                <div className="schedule-lesson-meta">
                                  {lesson.duration && <span className="tag-small">{lesson.duration} min</span>}
                                  <span className="tag-small">{getUnitNames(lesson.unitIds || (lesson.unitId ? [lesson.unitId] : []))}</span>
                                </div>
                              </div>
                              <div className="schedule-actions">
                                <button
                                  onClick={() => handleEdit(lesson)}
                                  className="btn-icon"
                                  title="Edit lesson"
                                >
                                  <Edit size={18} />
                                </button>
                              </div>
                            </div>
                            );
                          })
                        )}
                        {dateAssignments.length > 0 && (
                          dateAssignments.map((assignment) => (
                            <div
                              key={assignment.id}
                              className="schedule-lesson-item assignment-item"
                              style={{ borderLeft: '4px solid #f59e0b', backgroundColor: '#fef3c7' }}
                            >
                              <div className="schedule-lesson-info">
                                <div className="schedule-lesson-title">
                                  📝 {assignment.title}
                                </div>
                                <div className="schedule-lesson-meta">
                                  <span className="tag-small" style={{ backgroundColor: '#fed7aa', color: '#9a3412' }}>Assignment</span>
                                  {assignment.classNames && assignment.classNames.length > 0 && (
                                    <span className="tag-small">{assignment.classNames.join(', ')}</span>
                                  )}
                                  {assignment.dueDate && (
                                    <span className="tag-small" style={{ backgroundColor: '#fecaca', color: '#991b1b' }}>
                                      Due: {new Date(assignment.dueDate.toDate ? assignment.dueDate.toDate() : assignment.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                        {dateLessons.length === 0 && dateAssignments.length === 0 && (
                          <div className="schedule-no-lessons">
                            No lessons or assignments
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()
        ) : filteredLessons.length === 0 ? (
          <div className="empty-state">
            <BookOpen size={64} className="empty-icon" />
            <h3>No lessons yet</h3>
            <p>
              {viewMode === 'unit' && selectedUnit !== 'all'
                ? 'No lessons found for this unit'
                : 'Create your first lesson to get started'}
            </p>
            <button onClick={() => setShowModal(true)} className="btn-primary">
              <Plus size={20} />
              Create Lesson
            </button>
          </div>
        ) : (
          <div className="lessons-list">
            {filteredLessons
              .sort((a, b) => {
                // First sort by unit number
                const unitNumA = getUnitOrderNumber(a.unitIds || (a.unitId ? [a.unitId] : []));
                const unitNumB = getUnitOrderNumber(b.unitIds || (b.unitId ? [b.unitId] : []));

                if (unitNumA !== unitNumB) {
                  return unitNumA - unitNumB;
                }

                // Then sort by lesson number within the same unit
                const numA = parseInt(a.lessonNumber) || 999999;
                const numB = parseInt(b.lessonNumber) || 999999;
                return numA - numB;
              })
              .map((lesson, index) => {
              const isExpanded = expandedLessons[lesson.id];
              const hasResources = lesson.resources && lesson.resources.length > 0;
              const isDragOver = dragOverIndex === index;

              const unitColor = getUnitColor(lesson.unitIds || (lesson.unitId ? [lesson.unitId] : []));

              return (
                <div
                  key={lesson.id}
                  className={`lesson-card compact ${unitColor} ${isDragOver ? 'drag-over' : ''}`}
                  draggable={true}
                  onDragStart={(e) => handleUnitViewDragStart(e, lesson, index)}
                  onDragOver={(e) => handleUnitViewDragOver(e, index)}
                  onDragLeave={handleUnitViewDragLeave}
                  onDrop={(e) => handleUnitViewDrop(e, index)}
                >
                  <div className="lesson-header" onClick={() => toggleExpanded(lesson.id)}>
                    <div className="lesson-title-section">
                      <BookOpen className="lesson-icon" size={20} />
                      <div className="lesson-title-info">
                        <h3>
                          {lesson.lessonNumber && <span className="lesson-number">Lesson {lesson.lessonNumber}: </span>}
                          {lesson.title}
                        </h3>
                        <div className="lesson-meta-inline">
                          {lesson.duration && <span className="tag-small">{lesson.duration} min</span>}
                          {(lesson.startDate || lesson.scheduledDate) && (
                            <span className="tag-small">
                              {lesson.endDate && lesson.startDate !== lesson.endDate
                                ? `${new Date(lesson.startDate || lesson.scheduledDate).toLocaleDateString()} - ${new Date(lesson.endDate).toLocaleDateString()}`
                                : new Date(lesson.startDate || lesson.scheduledDate).toLocaleDateString()
                              }
                            </span>
                          )}
                          <span className="tag-small">{getUnitNames(lesson.unitIds || (lesson.unitId ? [lesson.unitId] : []))}</span>
                        </div>
                      </div>
                    </div>
                    <div className="card-actions">
                      {hasResources && (
                        <button
                          onClick={(e) => openResourcesMenu(lesson, e)}
                          className="btn-icon resources-btn"
                          title={`${lesson.resources.length} resource${lesson.resources.length > 1 ? 's' : ''}`}
                        >
                          <ExternalLink size={18} />
                          {lesson.resources.length > 1 && <span className="resource-count">{lesson.resources.length}</span>}
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(lesson);
                        }}
                        className="btn-icon"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(lesson.id);
                        }}
                        className="btn-icon delete"
                      >
                        <Trash2 size={18} />
                      </button>
                      <button className="btn-icon expand-btn">
                        {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                      </button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="lesson-details">
                      {(lesson.startDate || lesson.scheduledDate) && (
                        <div className="detail-section">
                          <strong>Date{lesson.endDate && lesson.startDate !== lesson.endDate ? ' Range' : ''}:</strong>
                          <p>
                            {lesson.endDate && lesson.startDate !== lesson.endDate
                              ? `${new Date(lesson.startDate || lesson.scheduledDate).toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })} - ${new Date(lesson.endDate).toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}`
                              : new Date(lesson.startDate || lesson.scheduledDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
                            }
                          </p>
                        </div>
                      )}

                      {lesson.learningObjective && (
                        <div className="detail-section">
                          <strong>Learning Objective:</strong>
                          <p>{lesson.learningObjective}</p>
                        </div>
                      )}

                      {lesson.lessonSummary && (
                        <div className="detail-section">
                          <strong>Summary:</strong>
                          <p>{lesson.lessonSummary}</p>
                        </div>
                      )}

                      {lesson.description && (
                        <div className="detail-section">
                          <strong>Notes:</strong>
                          <p>{lesson.description}</p>
                        </div>
                      )}

                      {lesson.objectives && lesson.objectives.length > 0 && (
                        <div className="detail-section">
                          <strong>Objectives:</strong>
                          <ul>
                            {lesson.objectives.map((obj, idx) => (
                              <li key={idx}>{obj}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {lesson.materials && lesson.materials.length > 0 && (
                        <div className="detail-section">
                          <strong>Materials:</strong>
                          <ul>
                            {lesson.materials.map((material, idx) => (
                              <li key={idx}>{material}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {lesson.procedures && (
                        <div className="detail-section">
                          <strong>Procedures:</strong>
                          <p className="procedures-text">{lesson.procedures}</p>
                        </div>
                      )}

                      {lesson.assessment && (
                        <div className="detail-section">
                          <strong>Assessment:</strong>
                          <p>{lesson.assessment}</p>
                        </div>
                      )}

                      {lesson.standards && lesson.standards.length > 0 && (
                        <div className="detail-section">
                          <strong>Standards:</strong>
                          <div className="standards-list">
                            {lesson.standards.map((standard, idx) => (
                              <span key={idx} className="tag">{standard}</span>
                            ))}
                          </div>
                        </div>
                      )}

                      {lesson.assignmentIds && lesson.assignmentIds.length > 0 && (
                        <div className="detail-section">
                          <strong>Linked Assignments:</strong>
                          <ul>
                            {lesson.assignmentIds.map((assignmentId) => {
                              const assignment = assignments.find(a => a.id === assignmentId);
                              return assignment ? (
                                <li key={assignmentId}>
                                  {assignment.title}
                                  {assignment.dueDate && (
                                    <span className="assignment-due-date"> (Due: {new Date(assignment.dueDate).toLocaleDateString()})</span>
                                  )}
                                </li>
                              ) : null;
                            })}
                          </ul>
                        </div>
                      )}

                      {hasResources && (
                        <div className="detail-section">
                          <strong>Resources:</strong>
                          <div className="resources-links">
                            {lesson.resources.map((resource, idx) => {
                              const resourceUrl = resource.fileUrl || resource.url;
                              return resourceUrl ? (
                                <a
                                  key={idx}
                                  href={resourceUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="resource-link"
                                >
                                  <ExternalLink size={14} />
                                  <span>{resource.description || `Resource ${idx + 1}`}</span>
                                  {resource.type && <span className="resource-type-badge">{resource.type}</span>}
                                </a>
                              ) : (
                                <div key={idx} className="resource-link disabled" title="Resource URL unavailable">
                                  <ExternalLink size={14} />
                                  <span>{resource.description || `Resource ${idx + 1}`}</span>
                                  <span className="resource-error">(Upload failed or URL missing)</span>
                                  {resource.type && <span className="resource-type-badge">{resource.type}</span>}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content lesson-modal" onClick={(e) => e.stopPropagation()}>
              <h2>{editingLesson ? 'Edit Lesson' : 'Create New Lesson'}</h2>
              <form onSubmit={handleSubmit} className="lesson-form">
                <div className="form-group">
                  <label>Units * (Select at least one)</label>
                  {units.length === 0 ? (
                    <p className="field-hint">No units found. Create a unit first.</p>
                  ) : (
                    <div className="checkbox-group">
                      {units.map(unit => (
                        <label key={unit.id} className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={formData.unitIds.includes(unit.id)}
                            onChange={() => handleUnitToggle(unit.id)}
                          />
                          <span>{unit.title}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label>Assignments (Optional)</label>
                  {assignments.length === 0 ? (
                    <p className="field-hint">No assignments found. Create an assignment first.</p>
                  ) : (
                    <div className="checkbox-group">
                      {assignments.map(assignment => (
                        <label key={assignment.id} className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={formData.assignmentIds.includes(assignment.id)}
                            onChange={() => handleAssignmentToggle(assignment.id)}
                          />
                          <span>{assignment.title}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group" style={{ flex: '0 0 150px' }}>
                    <label>Lesson Number</label>
                    <input
                      type="number"
                      value={formData.lessonNumber}
                      onChange={(e) => setFormData({ ...formData, lessonNumber: e.target.value })}
                      placeholder="e.g., 1"
                      min="0"
                      step="1"
                    />
                  </div>

                  <div className="form-group" style={{ flex: '1' }}>
                    <label>Lesson Title *</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                      placeholder="e.g., Introduction to Quadratic Equations"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Learning Objective *</label>
                  <input
                    type="text"
                    value={formData.learningObjective}
                    onChange={(e) => setFormData({ ...formData, learningObjective: e.target.value })}
                    required
                    placeholder="Main learning goal for this lesson (e.g., Students will understand the quadratic formula)"
                  />
                </div>

                <div className="form-group">
                  <label>Lesson Summary *</label>
                  <textarea
                    value={formData.lessonSummary}
                    onChange={(e) => setFormData({ ...formData, lessonSummary: e.target.value })}
                    required
                    rows="3"
                    placeholder="Brief summary of what will be covered in this lesson..."
                  />
                </div>

                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows="3"
                    placeholder="Additional notes or reminders for this lesson..."
                  />
                </div>

                <div className="form-group">
                  <label>Duration (minutes) *</label>
                  <input
                    type="number"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                    required
                    min="1"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Start Date</label>
                    <input
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label>End Date (optional)</label>
                    <input
                      type="date"
                      value={formData.endDate}
                      onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                      min={formData.startDate}
                    />
                  </div>
                </div>

                {/* Learning Objectives */}
                <div className="form-group array-field">
                  <label>Learning Objectives</label>
                  {formData.objectives.map((obj, index) => (
                    <div key={index} className="array-input-row">
                      <input
                        type="text"
                        value={obj}
                        onChange={(e) => handleArrayFieldChange('objectives', index, e.target.value)}
                        placeholder="Students will be able to..."
                      />
                      {formData.objectives.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeArrayField('objectives', index)}
                          className="btn-icon-small delete"
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addArrayField('objectives')}
                    className="add-field-btn"
                  >
                    + Add Objective
                  </button>
                </div>

                {/* Materials */}
                <div className="form-group array-field">
                  <label>Materials Needed</label>
                  {formData.materials.map((material, index) => (
                    <div key={index} className="array-input-row">
                      <input
                        type="text"
                        value={material}
                        onChange={(e) => handleArrayFieldChange('materials', index, e.target.value)}
                        placeholder="e.g., Whiteboard, Markers, Textbook"
                      />
                      {formData.materials.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeArrayField('materials', index)}
                          className="btn-icon-small delete"
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addArrayField('materials')}
                    className="add-field-btn"
                  >
                    + Add Material
                  </button>
                </div>

                {/* Procedures */}
                <div className="form-group">
                  <label>Procedures</label>
                  <textarea
                    value={formData.procedures}
                    onChange={(e) => setFormData({ ...formData, procedures: e.target.value })}
                    rows="6"
                    placeholder="Step-by-step lesson procedures..."
                  />
                </div>

                {/* Assessment */}
                <div className="form-group">
                  <label>Assessment</label>
                  <textarea
                    value={formData.assessment}
                    onChange={(e) => setFormData({ ...formData, assessment: e.target.value })}
                    rows="4"
                    placeholder="How will you assess student learning?"
                  />
                </div>

                {/* TEKS Standards */}
                <div className="form-group array-field">
                  <label>TEKS Standards</label>
                  {formData.standards.map((standard, index) => (
                    <div key={index} className="array-input-row">
                      <input
                        type="text"
                        value={standard}
                        onChange={(e) => handleArrayFieldChange('standards', index, e.target.value)}
                        placeholder="e.g., CCSS.MATH.CONTENT.HSA.REI.B.4"
                      />
                      {formData.standards.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeArrayField('standards', index)}
                          className="btn-icon-small delete"
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addArrayField('standards')}
                    className="add-field-btn"
                  >
                    + Add Standard
                  </button>
                </div>

                {/* Resources */}
                <div className="form-group">
                  <label>Resources</label>
                  {formData.resources.length === 0 ? (
                    <p className="field-hint" style={{ background: '#f3f4f6', color: '#6b7280' }}>
                      Add links, files, or Google resources to support this lesson
                    </p>
                  ) : (
                    <div className="resources-list">
                      {formData.resources.map((resource, index) => (
                        <div key={index} className="resource-item">
                          <div className="resource-header">
                            <select
                              value={resource.type}
                              onChange={(e) => updateResource(index, 'type', e.target.value)}
                              className="resource-type-select"
                            >
                              <option value="link">Link</option>
                              <option value="file">File Upload</option>
                              <option value="google">Google Resource</option>
                            </select>
                            <button
                              type="button"
                              onClick={() => removeResource(index)}
                              className="btn-icon-small delete"
                            >
                              <X size={16} />
                            </button>
                          </div>

                          <input
                            type="text"
                            value={resource.description}
                            onChange={(e) => updateResource(index, 'description', e.target.value)}
                            placeholder="Description (e.g., Video tutorial, Worksheet, Google Doc)"
                            className="resource-description"
                          />

                          {resource.type === 'file' ? (
                            <div className="file-upload-section">
                              {resource.fileUrl ? (
                                <div className="file-uploaded">
                                  <FileText size={16} />
                                  <span>{resource.fileName}</span>
                                  <a href={resource.fileUrl} target="_blank" rel="noopener noreferrer" className="view-file-btn">
                                    View File
                                  </a>
                                </div>
                              ) : (
                                <div className="file-input-wrapper">
                                  <Upload size={16} />
                                  <input
                                    type="file"
                                    onChange={(e) => handleFileUpload(index, e.target.files[0])}
                                    disabled={uploadingFile === index}
                                    className="file-input"
                                  />
                                  {uploadingFile === index && <span className="uploading-text">Uploading...</span>}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="url-input-wrapper">
                              <LinkIcon size={16} className="url-icon" />
                              <input
                                type="url"
                                value={resource.url}
                                onChange={(e) => updateResource(index, 'url', e.target.value)}
                                placeholder={
                                  resource.type === 'google'
                                    ? 'Google Drive/Docs link (e.g., https://docs.google.com/...)'
                                    : 'URL (e.g., https://example.com)'
                                }
                                className="resource-url"
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={addResource}
                    className="add-field-btn"
                  >
                    + Add Resource
                  </button>
                </div>

                <div className="modal-actions">
                  <button type="button" onClick={closeModal} className="btn-secondary">
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    {editingLesson ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default Lessons;
